import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { useAbaAtiva } from '../../layouts/dashboard/abas/AbasContexto';

/* ============================================================================
   Modal - o dialogo base do visual novo.

   Existe para que "abrir um formulario" seja sempre a mesma coisa: mesmo fundo,
   mesmo cabecalho, mesmo rodape de acoes, mesma saida pelo Esc. O `Dialog` do
   MUI fazia isso, mas arrasta o tema inteiro do MUI junto - e a migracao e
   justamente para sair dele.

   COMPORTAMENTOS QUE UM DIALOGO PRECISA TER E QUE COSTUMAM FALTAR:

   - ESC fecha. Sempre. Formulario sem saida de teclado prende quem nao usa mouse.
   - Clique no FUNDO fecha, mas clique DENTRO nao propaga - sem o stopPropagation
     o dialogo se fecha sozinho quando o usuario clica num campo.
   - O corpo da pagina para de rolar enquanto o modal esta aberto; sem isso o
     scroll "vaza" para a lista atras e o usuario perde o lugar onde estava.
   - O rodape so aparece quando ha acao. Rodape vazio e uma faixa cinza que nao
     faz nada.
   ============================================================================ */

export default function Modal({
  aberto,
  titulo,
  descricao,
  icone,
  largura = 'max-w-[560px]',
  onFechar,
  ocupado = false,
  children,
  rodape,
}: {
  aberto: boolean;
  titulo: string;
  descricao?: React.ReactNode;
  icone?: React.ReactNode;
  largura?: string;
  /** Em andamento: trava a janela e mostra a camada de espera. */
  ocupado?: boolean;
  onFechar: () => void;
  children: React.ReactNode;
  rodape?: React.ReactNode;
}) {
  /* ABA INATIVA NAO MEXE EM NADA GLOBAL - 25/08/2026, junto com as abas que
     mantem as telas montadas.

     Os dois efeitos abaixo saem da tela e valem para a JANELA inteira: travar o
     scroll do body e escutar Escape. Com as telas abertas ficando montadas, um
     modal aberto numa aba escondida continuava fazendo as duas coisas:

       - o scroll da aba VISIVEL ficava travado, sem nada na tela explicando;
       - Escape na aba visivel fechava um modal invisivel de outra aba.

     `useAbaAtiva()` responde `true` fora das abas (login, telas sem o layout do
     dashboard), entao nada muda nesses lugares. */
  const abaAtiva = useAbaAtiva();

  React.useEffect(() => {
    if (!aberto || !abaAtiva) return;
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && !ocupado && onFechar();
    window.addEventListener('keydown', aoTeclar);

    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', aoTeclar);
      document.body.style.overflow = anterior;
    };
  }, [aberto, abaAtiva, onFechar, ocupado]);

  if (!aberto) return null;

  return (
    /* Mesma trava do ModalPassos: enquanto a acao corre, a janela nao fecha por
       clique no fundo, por Escape nem pelo X. Ver o comentario la. */
    <div className="fixed inset-0 z-[1400] grid place-items-center bg-black/40 p-4" onClick={() => !ocupado && onFechar()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        aria-busy={ocupado}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          // `text-ink` pela mesma razao do Popover: bloco que pinta o proprio fundo
          // declara a propria cor de texto, senao herda a de quem o contem - e ha
          // partes do sistema (a faixa superior) com texto branco.
          'relative max-h-[90vh] w-full overflow-y-auto rounded-lg bg-surface text-ink shadow-card',
          largura
        )}
      >
        {/* CABECALHO IDENTICO AO DO ModalPassos - classe por classe.

            Os dois sao "modal do sistema": a mesma pessoa abre um e depois o outro
            no mesmo minuto, e qualquer diferenca de altura, de tamanho de titulo ou
            de cor de icone e lida como se fossem partes distintas do produto. Foi
            exatamente o que aconteceu quando este ficou azul e o outro claro.

            Se mexer aqui, mexa la tambem - ou extraia os dois para um componente
            so. Nao foi extraido ainda porque as estruturas de baixo divergem (um
            tem trilha de passos, o outro nao) e um componente com metade dos
            campos opcionais seria mais dificil de ler que a duplicata. */}
        <div className="flex items-center gap-3 border-b border-line bg-surface px-5 py-3">
          {icone && (
            <span className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-info-soft text-[12.5px] font-bold text-info">
              {icone}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[14px] font-extrabold tracking-[-0.02em]">{titulo}</h2>
            {descricao && <p className="truncate text-[12px] text-muted-foreground">{descricao}</p>}
          </div>
          <button
            type="button"
            onClick={onFechar}
            disabled={ocupado}
            className="flex-none text-[14px] text-faint hover:text-ink disabled:opacity-30"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 p-4">{children}</div>

        {/* Sem risco no topo: o fundo `surface-2` ja separa o rodape do corpo -
            mesma decisao do ModalPassos. */}
        {rodape && <div className="flex items-center justify-end gap-2 bg-surface-2 p-3">{rodape}</div>}

        {/* Camada de espera - `sticky` e nao `absolute` porque esta janela ROLA
            (`overflow-y-auto`): com `absolute` a camada cobriria a altura do
            conteudo, e num modal alto o aviso ficaria fora da vista, deixando a
            impressao de tela travada sem motivo. `sticky` com altura de viewport
            mantem o aviso onde a pessoa esta olhando.

            A margem negativa desfaz a altura que o proprio sticky ocuparia no
            fluxo - sem ela, a janela ganharia uma tela inteira de rolagem
            enquanto salva. */}
        {ocupado && (
          <div
            className="sticky inset-x-0 bottom-0 -mt-[100vh] grid h-[100vh] cursor-wait place-items-center"
            aria-hidden
          >
            <span className="absolute inset-0 bg-surface opacity-70" aria-hidden />
            <span className="relative flex items-center gap-2.5 rounded-lg bg-surface px-4 py-2.5 shadow-card">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="animate-spin" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.6" opacity="0.25" />
                <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
              </svg>
              <span className="text-[12.5px] font-semibold">Processando...</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Campos de formulario no padrao do visual novo. Ficam aqui, junto do Modal,
// porque e onde quase todo formulario do sistema vai viver.
// ----------------------------------------------------------------------------

/**
 * Dica de campo: icone que revela o texto ao passar o mouse (ou ao chegar de Tab).
 *
 * POR QUE VOLTOU A SER ICONE (19/08/2026): a dica era impressa como paragrafo
 * embaixo de cada campo. Numa tela de cadastro isso e uma linha extra; na politica,
 * onde ha ~40 campos com explicacao de duas a quatro linhas cada, o formulario
 * virou uma parede de texto cinza - a explicacao passou a ocupar mais altura que os
 * campos que ela explica, e as duas colunas desalinharam (campo com dica longa
 * empurra o vizinho para baixo).
 *
 * Nao e `title` do HTML: `title` so aceita string, e varias dicas do sistema tem
 * <strong>/<code> dentro; alem disso o navegador demora ~1s para mostrar e o texto
 * nao pode ser lido com o teclado. Aqui a bolha aparece no hover E no foco, o que
 * mantem a dica alcancavel sem mouse.
 *
 * `group-hover`/`group-focus-within` exigem a classe `group` no ancestral - ela
 * esta no <span> de fora. Sem ela as classes existem e nunca disparam.
 */
export function DicaCampo({ children }: { children: React.ReactNode }) {
  /* 🔴 DEFEITO CORRIGIDO (26/08/2026): "esta cortando o modal".

     A bolha era `position: absolute` dentro do campo. Isso funciona em tela aberta
     e NAO funciona dentro de modal: o card do modal e
     `max-h-[90vh] overflow-y-auto` (linha 84 deste arquivo), e `overflow` CORTA
     descendente posicionado. Em campo perto da borda esquerda, metade da dica
     desaparecia - foi o que apareceu no campo E-mail do cadastro de usuario.

     Mesma correcao aplicada no MenuAcoes no mesmo dia: a bolha vai para
     `document.body` por portal, com `position: fixed` medida do retangulo do
     icone. Fora do card, nao ha o que a corte.

     Isso obrigou a trocar `group-hover` (CSS) por estado: um elemento em portal
     nao e descendente do `group`, entao a classe nunca dispararia. Abre no mouse E
     no foco, como antes - dica alcancavel sem mouse continua sendo requisito. */
  const [aberta, setAberta] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const refIcone = React.useRef<HTMLSpanElement | null>(null);

  const LARGURA = 260;
  const MARGEM = 8;

  const medir = React.useCallback(() => {
    const r = refIcone.current?.getBoundingClientRect();
    if (!r) return;

    // Centralizada no icone, presa dentro da janela - era o `-translate-x-1/2`,
    // que sozinho nao sabe onde a janela termina.
    const centro = r.left + r.width / 2 - LARGURA / 2;
    const left = Math.max(MARGEM, Math.min(centro, window.innerWidth - LARGURA - MARGEM));

    // Vira para cima quando nao ha altura abaixo. 150px e a altura tipica da
    // bolha; se errar, o `maxHeight` abaixo evita passar da janela.
    const abaixo = window.innerHeight - r.bottom;
    setPos({ top: abaixo < 150 && r.top > 150 ? r.top - 6 - 150 : r.bottom + 6, left });
  }, []);

  const abrir = () => {
    medir();
    setAberta(true);
  };

  React.useEffect(() => {
    if (!aberta) return undefined;
    // `capture` para acompanhar a rolagem do proprio modal, que nao borbulha.
    document.addEventListener('scroll', medir, true);
    window.addEventListener('resize', medir);
    return () => {
      document.removeEventListener('scroll', medir, true);
      window.removeEventListener('resize', medir);
    };
  }, [aberta, medir]);

  return (
    <span
      className="relative ml-1 inline-flex align-middle"
      onMouseEnter={abrir}
      onMouseLeave={() => setAberta(false)}
      onFocus={abrir}
      onBlur={() => setAberta(false)}
    >
      {/* <span> e nao <button>: este icone vive dentro do <label> do campo, e clique
          em botao dentro de label dispara tambem o clique do label (o foco salta
          para o input). Com tabIndex=0 ele continua alcancavel pelo teclado. */}
      <span
        ref={refIcone}
        tabIndex={0}
        role="note"
        aria-label="Ajuda sobre este campo"
        className="grid h-[15px] w-[15px] cursor-help place-items-center rounded-full border border-line text-[10px] font-bold text-faint hover:border-primary hover:text-primary focus:border-primary focus:text-primary focus:outline-none"
      >
        i
      </span>
      {aberta &&
        pos &&
        createPortal(
      <span
        role="tooltip"
        style={{ top: pos.top, left: pos.left, maxHeight: '40vh' }}
        className="pointer-events-none fixed z-[1600] block w-[260px] overflow-y-auto rounded-lg border border-line bg-surface p-2.5 text-[11px] font-normal leading-[1.5] text-muted-foreground shadow-card"
      >
        {children}
      </span>,
          document.body
        )}
    </span>
  );
}

export function Campo({
  rotulo,
  obrigatorio,
  dica,
  /**
   * `true` volta a imprimir a dica embaixo do campo, em vez do icone.
   *
   * Existe para o punhado de dicas que NAO sao explicacao e sim aviso sobre o que
   * o campo faz com dado real ("o servidor recusa pacote+versionCode repetidos") -
   * aviso escondido atras de hover e aviso que ninguem le. Quem passa isto esta
   * dizendo "este texto precisa estar na tela".
   */
  dicaVisivel,
  /**
   * `true` desenha um `<div>` no lugar do `<label>`.
   *
   * OBRIGATORIO PARA WIDGET COMPOSTO (28/09/2026 - defeito relatado em
   * "Editar aplicativo > Selecionar grupos": "se clicar em qualquer canto
   * proximo desse selecionar grupo, ele apaga o que ja esta").
   *
   * Um `<label>` sem `htmlFor` ativa o PRIMEIRO descendente rotulavel quando se
   * clica em qualquer area dele - inclusive no texto do rotulo, na dica e no
   * espaco vazio. `<button>` e rotulavel. E no `AutoCompleteMultiplo` o
   * primeiro botao do DOM e o "x" da PRIMEIRA FICHA escolhida, porque as
   * fichas sao desenhadas ACIMA do campo de busca.
   *
   * Resultado: clicar em qualquer lugar solto do campo REMOVIA o primeiro grupo
   * selecionado. Quem escolhia dois grupos e clicava fora antes de salvar
   * perdia um, e parecia que "a selecao nao funciona".
   *
   * Nao dava para resolver dentro do AutoComplete: o `<label>` esta FORA dele.
   * Campo com UM controle so (input, select, textarea) continua `<label>`, que e
   * o certo - ali clicar no rotulo foca o campo, e e o comportamento desejado.
   */
  bloco,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  dica?: React.ReactNode;
  dicaVisivel?: boolean;
  bloco?: boolean;
  children: React.ReactNode;
}) {
  const Raiz: any = bloco ? 'div' : 'label';
  return (
    <Raiz className="block">
      <span className="mb-1 flex items-center text-[12px] font-semibold">
        {rotulo}
        {obrigatorio && <span className="ml-1 text-danger">*</span>}
        {dica && !dicaVisivel && <DicaCampo>{dica}</DicaCampo>}
      </span>
      {children}
      {dica && dicaVisivel && <span className="mt-1 block text-[11px] text-faint">{dica}</span>}
    </Raiz>
  );
}

// ----------------------------------------------------------------------------
// IconeOlho: mostrar/ocultar senha.
//
// Fica aqui, e nao em sections/auth, porque ha DOIS campos de senha no sistema em
// lugares sem relacao (as telas de auth e o "mudar senha" do dispositivo) e os
// dois traziam a palavra "ver"/"ocultar" escrita - largura variavel dentro do
// campo, competindo com o valor digitado. Icone unico, um arquivo so.
//
// `currentColor` para acompanhar o tema; quem chama poe o `aria-label` no botao,
// porque o icone em si nao carrega nome acessivel nenhum.
// ----------------------------------------------------------------------------
export function IconeOlho({ fechado, tamanho = 17 }: { fechado?: boolean; tamanho?: number }) {
  const traco = {
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M2.6 12S6 6.2 12 6.2 21.4 12 21.4 12 18 17.8 12 17.8 2.6 12 2.6 12z" {...traco} />
      <circle cx="12" cy="12" r="2.9" {...traco} />
      {fechado && <path d="M4 20L20 4" {...traco} />}
    </svg>
  );
}

export const classesEntrada =
  'h-9 w-full rounded-lg border border-line bg-surface px-3 text-[12.5px] text-ink outline-none placeholder:text-faint focus:border-primary';

/* DEFEITO CORRIGIDO (18/08/2026): nos dois botoes o `className` vinha DEPOIS do
   `{...props}`, entao ele sobrescrevia qualquer classe recebida - passar
   `className="w-full"` nao fazia nada, em silencio. Descobri ao reaproveitar o
   botao padrao no login, onde ele precisa ser largo e mais alto.

   Agora as classes sao MESCLADAS com `cn`: a base continua valendo e o chamador
   pode ajustar o que precisa (largura, altura), como em qualquer componente de
   biblioteca. `type` tambem saiu da frente do spread, para quem precisa de
   `type="submit"` conseguir. */
export function BotaoSecundario({
  children,
  className,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      {...props}
      className={cn(
        'h-9 rounded-lg border border-line px-3 text-[12.5px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40',
        className
      )}
    >
      {children}
    </button>
  );
}

export function BotaoPrimario({
  children,
  className,
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      {...props}
      className={cn(
        'h-9 rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-40',
        className
      )}
    >
      {children}
    </button>
  );
}

// ----------------------------------------------------------------------------
// AreaArquivo: escolher OU arrastar e soltar.
//
// POR QUE EXISTE: os tres lugares que sobem arquivo (nova APK, substituir APK,
// anexo da mensagem) usavam `<input type="file">` cru. O controle nativo tem dois
// problemas praticos: (1) nao aceita arrastar e soltar - quem baixou a APK e esta
// com a pasta aberta ao lado precisa clicar, navegar e reencontrar o arquivo; e
// (2) o rotulo do botao e do NAVEGADOR, nao do sistema ("Escolher ficheiro",
// "Nenhum ficheiro selecionado" no print do usuario), em portugues de Portugal e
// com aparencia que ignora o tema.
//
// O <input> continua ali, apenas invisivel dentro do <label>: e ele que abre o
// seletor no clique e no teclado (Enter/Espaco no label), entao nada de
// acessibilidade se perde para um `onClick` em <div>.
//
// `onDragOver` PRECISA de preventDefault: sem isso o navegador trata a pagina
// como destino invalido, nao dispara `onDrop` e ABRE o arquivo numa aba nova -
// que, com uma APK, e um download acidental.
// ----------------------------------------------------------------------------

/** Tamanho em unidade legivel. Base 1024, como o Explorer e o Finder mostram. */
export function tamanhoArquivo(bytes: number) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function AreaArquivo({
  arquivo,
  onArquivo,
  accept,
  disabled,
  rotulo = 'Arraste o arquivo aqui ou clique para escolher',
  dica,
  /**
   * Extensoes aceitas para a checagem do que foi SOLTO. `accept` do input so
   * filtra a janela do seletor - o arrastar e soltar passa por fora dele, entao
   * sem isto seria possivel soltar um .pdf num campo de .apk e so descobrir no
   * erro do servidor.
   */
  extensoes,
}: {
  arquivo: File | null;
  onArquivo: (f: File | null) => void;
  accept?: string;
  disabled?: boolean;
  rotulo?: string;
  dica?: React.ReactNode;
  extensoes?: string[];
}) {
  const [sobre, setSobre] = React.useState(false);
  const [recusado, setRecusado] = React.useState('');

  function aceitar(f: File | null) {
    if (!f) return;
    if (extensoes && extensoes.length > 0) {
      const nome = f.name.toLowerCase();
      if (!extensoes.some((e) => nome.endsWith(e.toLowerCase()))) {
        setRecusado(`Este campo aceita ${extensoes.join(', ')} — "${f.name}" não serve.`);
        return;
      }
    }
    setRecusado('');
    onArquivo(f);
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          if (disabled) return;
          e.preventDefault();
          setSobre(true);
        }}
        onDragLeave={() => setSobre(false)}
        onDrop={(e) => {
          if (disabled) return;
          e.preventDefault();
          setSobre(false);
          aceitar(e.dataTransfer.files?.[0] || null);
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-surface-2 px-4 py-6 text-center transition-colors',
          !disabled && 'hover:border-primary hover:bg-soft',
          sobre && 'border-primary bg-primary/10',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden className="text-primary">
          <path
            d="M12 16V4m0 0L8 8m4-4l4 4"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M4 15v2a3 3 0 003 3h10a3 3 0 003-3v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <span className="text-[12px] font-semibold text-ink">{sobre ? 'Solte para anexar' : rotulo}</span>
        {dica && <span className="text-[11px] text-faint">{dica}</span>}
        <input
          type="file"
          className="hidden"
          accept={accept}
          disabled={disabled}
          // `value=''` no clique: sem isto, escolher o MESMO arquivo de novo (depois
          // de remover) nao dispara `onChange` - o input compara com o valor atual.
          onClick={(e) => {
            (e.target as HTMLInputElement).value = '';
          }}
          onChange={(e) => aceitar(e.target.files?.[0] || null)}
        />
      </label>

      {recusado && <span className="mt-1.5 block text-[11px] font-semibold text-danger">{recusado}</span>}

      {arquivo && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-[12px]">
          <span className="min-w-0 flex-1 truncate font-semibold text-ink">{arquivo.name}</span>
          <span className="flex-none text-[11px] text-faint">{tamanhoArquivo(arquivo.size)}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => onArquivo(null)}
              className="flex-none text-[11px] font-bold text-danger hover:underline"
            >
              remover
            </button>
          )}
        </div>
      )}
    </div>
  );
}
